<template>
  <section class="proposals-read">
    <navigation-tabs
      :tabs="tabs"
      class="proposals-read-tabs"
    >
      <template v-slot:information>
        <context-state :state="state">
          <template v-slot:loading>
            Carregando proposta...
          </template>

          <template v-slot:notfound>
            Proposta não encontrada :(
          </template>
        </context-state>

        <proposals-read-information v-if="proposal.id && Object.keys(attempts).length" />
      </template>

      <template v-slot:comments>
        <proposals-read-comments />
      </template>

      <template v-slot:log>
        <proposals-read-log
          v-if="proposal.id"
          :parent="'logProposals'"
        />
      </template>
    </navigation-tabs>
  </section>
</template>

<script>
  import { mapState } from 'vuex';

  import PAGINATION from '@sdk/helpers/misc/pagination';

  import { attemptsChannel, client as pusherClient } from '@sdk/helpers/misc/pusher';

  import ContextState from '@cordel/components/Context/State.vue';

  import NavigationTabs from '@cordel/components/Navigation/Tabs.vue';

  import ProposalsReadActions from '@sdk/views/Proposals/Read/Actions.vue';
  import ProposalsReadInformation from '@sdk/views/Proposals/Read/Information.vue';
  import ProposalsReadLog from '@sdk/views/Proposals/Read/Log.vue';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import ProposalsReadComments from '@/views/Proposals/Read/Comments.vue';

  export default {
    name: 'ProposalsRead',
    components: {
      ContextState,

      NavigationTabs,

      ProposalsReadInformation,
      ProposalsReadComments,
      ProposalsReadLog,
    },
    data() {
      return {
        scroll: null,
        scrolled: false,

        channel: null,

        nav: {
          action: ProposalsReadActions,
        },
      };
    },
    computed: {
      ...mapState([
        'auth',
        'customers',
        'proposals',
        'proposalAttempts',
      ]),

      account() {
        return this.auth.user.account.id;
      },

      state() {
        const {
          loading,
          item,
        } = this.proposals.read;

        if (loading) {
          return 'loading';
        }

        if (item.id) {
          return null;
        }

        return 'notfound';
      },

      proposal() {
        return this.proposals.read.item;
      },
      customer() {
        return this.customers.read.item;
      },

      attempts() {
        return this.proposalAttempts.list.items;
      },

      tabs() {
        const comments = this.proposal.comments_count;
        const activities = this.proposal.activities_count;

        return [{
          label: 'Informações',
          slot: 'information',
        }, {
          label: comments ? `Comentários (${comments})` : 'Comentários',
          slot: 'comments',
          initial: /comments/.test(this.$route.path),
        }, {
          label: activities ? `Log de atividades (${activities})` : 'Log de atividades',
          slot: 'log',
        }];
      },
    },
    watch: {
      // O Vue Router REAPROVEITA este componente quando só o param `:id` muda
      // (proposta A → proposta B na mesma rota): mounted e beforeDestroy não
      // rodam de novo. Sem isto a assinatura ficaria presa no canal montado na
      // entrada — o da proposta anterior — e a nova não receberia evento nenhum.
      // O polling que este canal substituiu não tinha esse buraco, porque o
      // timer relia $route.params.id a cada tick.
      '$route.params.id': function reenter() {
        this.leave();
        this.enter();
      },
    },
    mounted() {
      this.enter();
    },
    beforeDestroy() {
      this.leave();
    },
    methods: {
      // Entrar numa proposta: pela montagem do componente ou pela troca do
      // param. Um lugar só, para que os dois caminhos não divirjam.
      enter() {
        this.$store.dispatch('simulations/clear', { action: 'read' });
        this.$store.dispatch('customers/clear', { action: 'read' });
        this.$store.dispatch('proposals/clear', { action: 'read' });
        // A list de tentativas não se esvazia mais sozinha a cada busca (ver
        // store/modules/proposal-attempts.js), então quem entra na tela limpa aqui —
        // senão a proposta nova herdaria as tentativas da anterior até a resposta chegar.
        this.$store.dispatch('proposalAttempts/clear', { action: 'list' });

        // O scrollIntoView do hash é uma vez por proposta, e o leave() o desarma
        // — quem entra rearma.
        this.scrolled = false;

        this.getProposal();
        this.getAttempts();
        this.watchAttempts();
      },

      leave() {
        // Mesma trava que o contador de iterações fazia: lista que chegar depois
        // da saída não agenda mais nada.
        this.scrolled = true;

        clearTimeout(this.scroll);

        this.unwatchAttempts();
      },

      getProposal() {
        return this.$store.dispatch('proposals/read', {
          id: this.$route.params.id,
        })
        .then(this.gotProposal);
      },
      gotProposal() {
        const rule = {
          store_id: this.proposals.read.item.store.id,
          vehicle_model_id: this.proposals.read.item.vehicle_model.id,
        };

        this.$store.dispatch('financingReturnRules/findForIntegratedBanks', rule);
        this.$store.dispatch('financingProductRules/findForIntegratedBanks', rule);

        return this.$store.dispatch('customers/read', {
          id: this.proposals.read.item.customer.id,
        });
      },

      getAttempts() {
        const {
          dispatch,
        } = this.$store;

        // Toda busca substitui a lista. O append existia porque a partir da 2ª
        // ida o gatilho era o polling, e o merge do CRUDL faz o item que já está
        // na lista vencer o que veio do servidor ({ ...novos, ...atuais }) — ou
        // seja, ele só deixava aparecer tentativa nova e descartava justamente a
        // mudança de estado que este canal anuncia. Como a busca traz a lista
        // inteira da proposta (per_page MAX), substituir não perde nada.
        dispatch('proposalAttempts/filter', {
          CRUDL_append: false,
        });

        return dispatch('proposalAttempts/list', {
          proposal_id: this.$route.params.id,
          per_page: PAGINATION.MAX_PER_PAGE,
        }).then(this.gotAttempts);
      },
      gotAttempts() {
        if (this.scrolled) {
          return;
        }

        // O scrollIntoView do hash é uma vez só, na primeira lista que chega.
        this.scrolled = true;
        this.scroll = setTimeout(this.showAttempt, 1000);
      },

      // Assina as transições de estado das tentativas desta proposta no lugar do
      // polling. O payload do evento é INVALIDATE — diz só QUE mudou —, então a
      // tela refaz o mesmo GET e a visibilidade continua sendo decidida pela
      // policy no servidor.
      watchAttempts() {
        const pusher = pusherClient();

        if (!pusher) {
          // Em produção a pwa é sempre servida de dentro do web, então o cliente
          // do host está lá: faltar é cenário de dev standalone. Avisa e para -
          // caminho alternativo para cenário que produção não tem é código que
          // ninguém exercita e ninguém lembra de apagar.
          console.warn('sem window.CDRWEB.pusher.client: as tentativas não se atualizam sozinhas');
          return;
        }

        this.channel = attemptsChannel(this.account, this.$route.params.id);

        pusher.subscribe(this.channel).bind('status', this.getAttempts);

        // O Pusher não faz replay: o que passou com a conexão caída não chega
        // depois. "connected" dispara na conexão inicial e em toda reconexão,
        // que é exatamente quando pode haver buraco.
        pusher.connection.bind('connected', this.getAttempts);

        document.addEventListener('visibilitychange', this.refetchOnVisible);
      },

      // Aba que volta a ficar visível só refaz a busca se a conexão NÃO está de
      // pé — com ela de pé o "connected" acima já cobriu, e isto seria um
      // request a cada alt+tab.
      refetchOnVisible() {
        const pusher = pusherClient();

        if (document.visibilityState !== 'visible') {
          return;
        }

        if (pusher && pusher.connection.state === 'connected') {
          return;
        }

        this.getAttempts();
      },

      unwatchAttempts() {
        const pusher = pusherClient();

        document.removeEventListener('visibilitychange', this.refetchOnVisible);

        if (!pusher || !this.channel) {
          return;
        }

        pusher.connection.unbind('connected', this.getAttempts);

        const channel = pusher.channel(this.channel);

        if (channel) {
          channel.unbind('status', this.getAttempts);
        }

        // Quem tira o canal do registro é o CLIENTE, e o cliente aqui é o do
        // host: ele sobrevive à navegação da SPA, então canal não removido volta
        // a ser inscrito a cada reconnect.
        pusher.unsubscribe(this.channel);
        this.channel = null;
      },
      showAttempt() {
        if (/#attempt-/.test(this.$route.hash)) {
          const attempt = document.getElementById(this.$route.hash.replace('#', ''));

          if (attempt) {
            attempt.scrollIntoView({
              block: 'start',
              behavior: 'smooth',
            });
          }
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .proposals-read {
  }

  .proposals-read-tabs {
    margin: 0 var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: 0 var(--product--pwa--horizontal);
    }
  }
</style>
